import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout'
import Head from 'next/head'
import Error from 'next/error'
import NextLink from 'next/link'
import { useRouter } from 'next/router';
import Container from '../../components/Container';
import ExternalLink from '../../components/ExternalLink';
import { Box, LinkBox, Button, Center, Heading, Flex, Spinner, Text, Link, List, ListItem, ListIcon, LinkOverlay, Badge, Divider, Wrap, WrapItem, Stack, HStack, VStack, useColorModeValue, Spacer, SimpleGrid, Container as ChakraContainer } from "@chakra-ui/react";
import { IoGitNetworkOutline, IoChevronForwardCircle } from "react-icons/io5";
import prisma from '../../lib/prisma'
import { useUser } from "@clerk/nextjs";
import useSWR from "swr";

const fetcher = (url) =>
  fetch(url, {
    method: 'GET',
    headers: new Headers({ 'Content-Type': 'application/json'}),
    // credentials: 'same-origin',
  }).then((res) => res.json())

export default function Phenotype({ errorCode, phenotype }) {
    const { user } = useUser()
    const router = useRouter();
    // console.log(router);
    if (!phenotype) {
        return <h1>Sorry, phenotype not found!</h1>
    }

    if (errorCode) {
        return <Error statusCode={errorCode} />
    }
    const borderColor = useColorModeValue("gray.200", "neutral.900")
    function formatNumber(num) {
        return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
      }

    const cardBg = useColorModeValue("gray.100", "neutral.1000")

    function useUserPhenotype() {
        const { data, error, isValidating } = useSWR(`/api/user/getPhenotype?phenotypeId=${phenotype.id}`,fetcher, {dedupingInterval: 15000, revalidateOnFocus: false })
        // console.log(error);
        return {
          data: data,
          isLoading: !error && !data,
          isError: error
        }
      }

      const { data: userPhenotype, isLoading: userPhenotypeLoading, isError: userPhenotypeError } = useUserPhenotype()
    //   console.log(userPhenotype);
    return (
        <>
    <Layout>
    <Head>
        <title>{phenotype.name}</title>
        <meta name="robots" content="index, follow"></meta>
      </Head>
    <Container>
        <ChakraContainer maxW={'6xl'} mb={100}>
        <Flex columns={3} mt={70}>
            <Box>
                <Stack w={['80%', 'full']}>
                <Heading as="h1" fontSize="3xl" mt={70}>{phenotype.name}</Heading>
                </Stack>
            </Box>
            <Spacer />
            {user ? 
            <Box border={'1px'} borderRadius={10} p={5} borderColor={'gray.200'}>
                <Center>
                <VStack>
                <Heading size="md">Your Results</Heading>
                
                {userPhenotypeLoading ? <Spinner color="cyan.500" size="lg" thickness="3px"/> :
                <Box>
                {userPhenotype.results.length > 0 ? 
                <Box>
                    <Center my="3">
                {userPhenotype.results[0].significance.length > 0 ?
                userPhenotype.results[0].significance.map((significance, index) => <Badge key={index} px={3} py={1} borderRadius={30} fontSize={"xs"} colorScheme={significance == 'risk' ? 'red' : significance == 'carrier' ? 'orange' : 'gray'}>{significance}</Badge>)
                :     <Badge px={3} py={1} borderRadius={30} fontSize={"xs"}>Undetermined</Badge>
            }
                </Center>
                <Divider my={5}/>
                <Center>
                <NextLink href={`${router.asPath}/my-variants`} passHref>
                <Link fontSize={'sm'} fontWeight={'bold'}>{userPhenotype.genotypeCount} {userPhenotype.genotypeCount > 1 ? 'VARIANTS' : 'VARIANT'} →</Link>
                </NextLink>
                </Center>
                </Box>
                :
                <Text fontSize="13">You are not associated with this phenotype</Text>
                }
                </Box>
                }

                </VStack>
                </Center>
                
            </Box>
            : 
            <Box border={'1px'} borderRadius={10} p={5} borderColor={'gray.200'}>
                
            <Center>
            <VStack>
            <Heading size="md">Your Results</Heading>
            <Button
                as={'a'}
                fontSize={'sm'}
                fontWeight={600}
                variant={'link'}
                href={'/signin'}>
                Sign In
                </Button>
            
            </VStack>
            </Center>
            
        </Box>
        }
        </Flex>
        {/* <Heading as="h1" fontSize="3xl" mt={70}>{phenotype.name}</Heading> */}
        {/* <Heading as="h2" fontSize="xl" mt={2}>{gene.name}</Heading> */}
        {/* {gene.alias_symbols.length > 0 ? 
        <Box mt={7}>
            <Text fontSize="sm" fontWeight="bold" as="h3">ALIAS SYMBOLS</Text>
            <Wrap>
            {gene.alias_symbols.map(alias => <WrapItem key={alias}><Text fontSize="sm" fontWeight="normal" borderRadius={'lg'}>{alias}</Text></WrapItem>)}
            </Wrap>
        </Box>
        : null} */}

        <Divider mb={10} mt={10}/>
        <Flex
            direction={['column-reverse', 'column-reverse', 'row', 'row']}
            w="full"
            justify="space-between"
        >
            <Box mt={[8, 8, 0]} mb={3} minW={['', 'xl']} maxW={['2xl', '2xl', '2lg', '3xl']}>
                <Heading as="h3" mb={3} fontSize="lg" fontWeight="bold">Description</Heading>
                <Box width={['100%','100%', '90%']}>{phenotype.medgen.map(medgen => <div><p>{medgen.definition}<ExternalLink fontSize={'sm'} link={`${medgen.definition_source_url}`} linktext={medgen.definition_source} /></p></div>)}</Box>
            </Box>
            <Flex flexDirection={["row", "row", "column", "column"]}>
            <Box border="1px" p={5} borderRadius={10} borderColor={borderColor} w={['100%', '100%']}>
            <Wrap align="center" mb={4}><WrapItem><IoGitNetworkOutline style={{fontSize: '25', }}/></WrapItem><WrapItem><Heading fontSize="16">Mode of Inheritance</Heading></WrapItem></Wrap>
            <List spacing={3}>
                {phenotype.mois.map(moi => 
                <ListItem>
                <ListIcon as={IoChevronForwardCircle} color="cyan.500" />
                {moi.moi.moi}
                </ListItem>
                    )}
                </List>
            </Box>

            <Flex flexDirection={["column", "column"]} mt={[0,0,4]} ml={['10%','25%',0]} bg={cardBg} w={['50%', '100%']} borderRadius={10} p={5} textAlign={["right", "right","right"]}>
                <Box mb={[0,0,16]}>
                <Text fontSize="12" color="gray.500" fontWeight="bold">VARIANTS</Text>
                <Text fontSize="3xl" fontWeight="normal">{formatNumber(phenotype._count.variants)}</Text>
                </Box>
                <Box  marginTop="auto" textAlign="right">
                {phenotype._count.variants > 0 ? 
                <Text fontSize="sm" fontWeight="bold"  bottom='5' right='5'>
                <Link as={NextLink} href={`/phenotypes/${phenotype.slug}/variants`}>SEE THE VARIANTS →</Link>
                </Text>

                : null}
                </Box>
            </Flex>
            </Flex>
            </Flex>
            <Flex
            direction={['column-reverse', 'column-reverse', 'row', 'row']}
            w="full"
            justify="space-between"

            >
            <Box mt={[8, 8, 30]} mb={3} minW={['', 'xl']} maxW={'2xl'}>
            <Heading as="h3" mb={3} fontSize="lg" fontWeight="bold">Genes</Heading>
                <Wrap>
                    {phenotype.genes.map(gene => 
                    <LinkBox as="div">
                    <WrapItem key={gene.symbol}>
                        
                            
                                <Badge ml="0" colorScheme={gene.genes ? 'cyan' : 'gray'} borderRadius="lg" pl={2} pr={2} pt={2} pb={2} textTransform="capitalize">
                                {gene.genes ? 
                                <NextLink href={`/genes/${gene.genes.slug}`} passHref>
                                    <LinkOverlay>
                                        {gene.genes.symbol}
                                    </LinkOverlay>
                                    </NextLink>
                            : 
                            <Text>{gene.symbol}</Text>
                         }
                                </Badge>
                    </WrapItem>
                    </LinkBox>
                    )}
                    
                </Wrap>
            </Box>
            </Flex>
            <Box mt={10}>
            <Heading as="h3" mb={5} fontSize="lg" fontWeight="bold">External Links</Heading>
            <Wrap maxW={['','3xl']}>
            <WrapItem>
            <Box borderLeft="1px" pl={3} borderColor={borderColor}>
            <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">OMIM</Text>
            {phenotype.omim.map(omim => 
            <ExternalLink 
                fontSize={'sm'} 
                link={`https://omim.org/entry/${omim.mim_id}`} 
                linktext={omim.mim_id} 
            />
            )}
            </Box>
            </WrapItem>
            <WrapItem>
            <Box borderLeft="1px" pl={3} borderColor={borderColor}>
            <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">Orphanet</Text>
            {phenotype.orphanet.map(orphanet => 
            <ExternalLink 
                fontSize={'sm'} 
                link={`https://www.orpha.net/consor/cgi-bin/OC_Exp.php?lng=EN&Expert=${orphanet.orphanet_id}`} 
                linktext={orphanet.orphanet_id} 
            />
            )}
            </Box>
            </WrapItem>
            <WrapItem>
            <Box borderLeft="1px" pl={3} borderColor={borderColor}>
            <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">HPO</Text>
            {phenotype.hpo.map(hpo => 
            <ExternalLink 
                fontSize={'sm'} 
                link={`https://hpo.jax.org/app/browse/term/${hpo.id}`} 
                linktext={hpo.id} 
            />
            )}
            </Box>
            </WrapItem>
            <WrapItem>
            <Box borderLeft="1px" pl={3} borderColor={borderColor}>
            <Text as="h5" fontWeight="normal" fontSize={'sm'} color="gray.500">Medgen</Text>
            {phenotype.medgen.map(medgen => 
            <ExternalLink 
                fontSize={'sm'} 
                link={`https://www.ncbi.nlm.nih.gov/medgen/${medgen.medgen_id}`} 
                linktext={medgen.medgen_id} 
            />
            )}
            </Box>
            </WrapItem>
            </Wrap>
            </Box>

        </ChakraContainer>

        </Container>
        
    </Layout>
    
    </>
    )
}

export async function getServerSideProps(context) {
    var queryArgs = {}
    queryArgs = {
        slug: context.params.slug
    }
  
    const [phenotype] = await prisma.$transaction([
      prisma.phenotypes.findUnique({
        where: queryArgs,
        include: {
          genes: {
            select: {
              symbol: true,
              genes: {
                  select: {
                      symbol: true,
                      slug: true
                  }
              }
            }
          },
          _count: {
            select: { variants: true },
          },
          medgen: {
              select: {
                  medgen_id: true,
                  definition: true,
                  definition_source: true,
                  definition_source_url: true
              }
          },
          omim: true,
          orphanet: {
            select: {
                orphanet_id: true
            }
          },
          hpo: true,
          mois: {
              select: {
                moi: true
              }
          }
        },
      })
    ])

    if (!phenotype) {
        return {
            notFound: true
        }
    }

    // console.log(phenotype)
  
    const data = JSON.stringify(phenotype, (key, value) => (typeof value === 'bigint' ? value.toString() : value))
    return { props: { phenotype: JSON.parse(data) } }
    }
